import { useCallback, useState } from 'react';

import { ArcherContainer, ArcherElement } from 'react-archer';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router';

import PlainFieldset from '@/components/atoms/Fieldset/templates/PlainFieldset';
import IfIcon from '@/components/atoms/Icons/IfIcon';
import { MODAL_AI_AGENT_CREATE } from '@/components/organisms/Modals/ModalConductor';
import { TileCreation } from '@/components/organisms/Tile';
import Tile from '@/components/organisms/Tile/Tile';
import { Button } from '@/components/ui/button';
import useBrains from '@/hooks/useBrains';
import { useDeskId } from '@/hooks/useDeskId';
import { cn } from '@/lib/utils';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { pushModal } from '@/redux/modals/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { selectIframeView } from '@/redux/user/selectors';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';

import { FIELDSET_CARD_PADDING } from '../../constants';
import BrainTile from './BrainTile/BrainTile';
import HGSPulse from './HGSPulse';
import IntegrationTile from './IntegrationTile/IntegrationTile';
import MoveoPulse from './MoveoPulse';
import useHomeOverview from './useHomeOverview';

import styles from './HomeOverview.module.scss';

const DEFAULT_ITEMS_LIMIT = 5;

const HomeOverview = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const deskId = useDeskId();
  const {
    rulesWithBrains,
    integrations,
    active,
    setActive,
    shouldHighlightIntegration,
    isLoading,
  } = useHomeOverview();
  const isIframeView = useSelector(selectIframeView);
  const { brains } = useBrains();
  const [brainsLimit, setBrainsLImit] = useState(DEFAULT_ITEMS_LIMIT);
  const [integrationsLimit, setIntegrationsLImit] = useState(DEFAULT_ITEMS_LIMIT);

  const features = useSelector(selectFeatureFlags);
  const { t } = useTranslation();

  const slug = useSelector(selectAccountSlug);

  const handleMouseOver = useCallback(
    (index: number) => () => {
      setActive(index);
    },
    [setActive]
  );

  const handleMouseOut = useCallback(() => {
    setActive(null);
  }, [setActive]);

  const showAddBrainModal = useCallback(() => {
    if (brains?.length === 0) {
      trackEvent(EventName.ClickCreateBrain);
      dispatch(pushModal(MODAL_AI_AGENT_CREATE));
      navigate(`/${slug}/ai-agents`);
    } else {
      navigate(`/${slug}/environments/${deskId}/rules`);
    }
  }, [brains?.length, deskId, dispatch, slug, navigate]);

  const onCreateChannel = () => {
    navigate(`/${slug}/environments/${deskId}/integrations`);
  };

  const updatedRulesWithBrains = rulesWithBrains?.slice(0, brainsLimit);
  const updatedIntegrations = integrations?.slice(0, integrationsLimit);

  // Hide the component if there are not desk ids
  if (!deskId) {
    return null;
  }

  return (
    <PlainFieldset fullWidth isLoading={isLoading} className={FIELDSET_CARD_PADDING}>
      <ArcherContainer>
        <div className={styles.wrapper}>
          <div className={styles.wrapperBrains}>
            <span className={cn('text-sm leading-5 font-normal', styles.title)}>
              {t('home.content')}
            </span>
            {updatedRulesWithBrains?.map(
              ({ brain, brain_version, rule_id, collection_id, collection_name }, index) => {
                return (
                  <ArcherElement
                    id={`brain-${index}`}
                    key={`${brain?.brain_parent_id}-${rule_id}`}
                    relations={[
                      {
                        targetId: `rule-${index}`,
                        targetAnchor: 'left',
                        sourceAnchor: 'right',
                        style: {
                          endMarker: false,
                          strokeWidth: index === active ? 2 : 1,
                          strokeColor:
                            index === active
                              ? 'color-mix(in srgb, var(--primary) 50%, transparent)'
                              : 'var(--border)',
                        },
                      },
                    ]}
                  >
                    <div
                      onMouseOver={handleMouseOver(index)}
                      onMouseOut={handleMouseOut}
                      onFocus={handleMouseOver(index)}
                      onBlur={handleMouseOut}
                    >
                      <BrainTile
                        brain={brain}
                        brain_version={brain_version}
                        collection_id={collection_id}
                        collection_name={collection_name}
                      />
                    </div>
                  </ArcherElement>
                );
              }
            )}
            {(updatedRulesWithBrains?.length === 0 || brains?.length === 0) && (
              <ArcherElement
                id="brain-empty"
                relations={[
                  {
                    targetId: 'moveo',
                    targetAnchor: 'left',
                    sourceAnchor: 'right',
                    style: {
                      endMarker: false,
                      strokeWidth: 1,
                      strokeColor: 'color-mix(in srgb, var(--primary) 50%, transparent)',
                      strokeDasharray: '5,5',
                    },
                  },
                ]}
              >
                <span className={styles.createTile}>
                  <TileCreation
                    size="small"
                    onCreateClick={showAddBrainModal}
                    title={brains?.length === 0 ? t('brains.add_brain') : t('brains.connect_brain')}
                  />
                </span>
              </ArcherElement>
            )}

            {rulesWithBrains?.length > DEFAULT_ITEMS_LIMIT && (
              <Button
                variant="ghost"
                onClick={() => setBrainsLImit((prev) => (prev ? undefined : DEFAULT_ITEMS_LIMIT))}
              >
                {t('home.see')} {brainsLimit ? t('home.more') : t('home.less')}
              </Button>
            )}
          </div>
          <div className={styles.wrapperRules}>
            {updatedRulesWithBrains?.map((rule, index) => (
              <ArcherElement
                id={`rule-${index}`}
                key={rule.rule_id}
                relations={[
                  {
                    targetId: 'moveo',
                    targetAnchor: 'left',
                    sourceAnchor: 'right',
                    style: {
                      endMarker: false,
                      strokeWidth: index === active ? 2 : 1,
                      strokeColor:
                        index === active
                          ? 'color-mix(in srgb, var(--primary) 50%, transparent)'
                          : 'var(--border)',
                    },
                  },
                ]}
              >
                <div
                  className={cn(styles.ruleTileWrapper, {
                    [styles.hasCollections]: !features?.enable_collections_for_rules,
                  })}
                >
                  <Link to={`/${slug}/environments/${deskId}/rules/${rule.rule_id}`}>
                    <Tile className={styles.tile}>
                      <div
                        className={styles.ruleTile}
                        onMouseOver={handleMouseOver(index)}
                        onMouseOut={handleMouseOut}
                        onFocus={handleMouseOver(index)}
                        onBlur={handleMouseOut}
                      >
                        <IfIcon size={36} className="text-muted-foreground" />
                      </div>
                    </Tile>
                  </Link>
                </div>
              </ArcherElement>
            ))}
          </div>

          <ArcherElement id="moveo">
            <div>
              {isIframeView ? (
                <HGSPulse />
              ) : (
                <MoveoPulse
                  active={updatedRulesWithBrains?.length > 0 && integrations?.length > 0}
                />
              )}
            </div>
          </ArcherElement>

          <div className={styles.wrapperChannels}>
            <span className={cn('text-sm leading-5 font-normal', styles.title)}>
              {t('common.connections')}
            </span>
            {updatedIntegrations?.map((integration, index) => {
              return (
                <ArcherElement
                  id={`integration-${index}`}
                  key={integration.integration_id}
                  relations={[
                    {
                      targetId: 'moveo',
                      targetAnchor: 'right',
                      sourceAnchor: 'left',
                      style: {
                        endMarker: false,
                        strokeWidth: shouldHighlightIntegration(index) ? 2 : 1,
                        strokeColor: shouldHighlightIntegration(index)
                          ? 'color-mix(in srgb, var(--primary) 50%, transparent)'
                          : 'var(--border)',
                      },
                    },
                  ]}
                >
                  <div>
                    <IntegrationTile integration={integration} />
                  </div>
                </ArcherElement>
              );
            })}

            {integrations?.length === 0 && (
              <ArcherElement
                id="channels-empty"
                relations={[
                  {
                    targetId: 'moveo',
                    targetAnchor: 'right',
                    sourceAnchor: 'left',
                    style: {
                      endMarker: false,
                      strokeWidth: 1,
                      strokeColor: 'color-mix(in srgb, var(--primary) 50%, transparent)',
                      strokeDasharray: '5,5',
                    },
                  },
                ]}
              >
                <span className={styles.createTile}>
                  <TileCreation
                    size="small"
                    onCreateClick={onCreateChannel}
                    title={t('home.add_channel')}
                  />
                </span>
              </ArcherElement>
            )}

            {integrations?.length > DEFAULT_ITEMS_LIMIT && (
              <Button
                variant="ghost"
                onClick={() =>
                  setIntegrationsLImit((prev) => (prev ? undefined : DEFAULT_ITEMS_LIMIT))
                }
              >
                {t('home.see')} {integrationsLimit ? t('home.more') : t('home.less')}
              </Button>
            )}
          </div>
        </div>
      </ArcherContainer>
    </PlainFieldset>
  );
};

export default HomeOverview;
